import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MediaItem, SessionSettings } from '@wallflower/shared';

function shuffled<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j] as T, copy[i] as T];
  }
  return copy;
}

export interface SlideshowState {
  current: MediaItem | null;
  /** The next couple of items, so the stage can preload them. */
  upcoming: MediaItem[];
  /**
   * Increments on every advance, even when the same item comes round again.
   * The stage keys its layer on this, so a lone photo restarts its ken-burns
   * and a lone video replays instead of freezing on its last frame.
   */
  slideKey: number;
  advance: () => void;
}

/**
 * Playlist scheduling. Plays through the whole pool before repeating anything,
 * and (when enabled) lets a photo that just arrived cut the line so a guest sees
 * their upload on the TV while they are still looking at it.
 */
export function useSlideshow(media: MediaItem[], settings: SessionSettings | null): SlideshowState {
  const pool = useMemo(() => media.filter((m) => m.status === 'ready' && !m.hidden), [media]);
  const poolRef = useRef(pool);
  poolRef.current = pool;

  const [current, setCurrent] = useState<MediaItem | null>(null);
  // Bumped on every advance. Without it, re-showing the same item would be a
  // no-op state update, React would skip the render, and the effect that
  // schedules the next slide would never re-run - the slideshow would stop.
  const [slideKey, setSlideKey] = useState(0);
  const [upcomingIds, setUpcomingIds] = useState<string[]>([]);
  const queueRef = useRef<string[]>([]);
  const priorityRef = useRef<string[]>([]);
  const knownIdsRef = useRef<Set<string> | null>(null);

  const shuffle = settings?.shuffle ?? false;
  const newMediaPriority = settings?.newMediaPriority ?? true;
  const slideDurationMs = settings?.slideDurationMs ?? 8000;

  const refill = useCallback(
    (excludeId?: string) => {
      const ids = poolRef.current.map((m) => m.id).filter((id) => id !== excludeId);
      const ordered = shuffle
        ? shuffled(ids)
        : [...poolRef.current]
            .sort((a, b) => a.createdAt - b.createdAt)
            .map((m) => m.id)
            .filter((id) => id !== excludeId);
      queueRef.current = ordered;
    },
    [shuffle],
  );

  const advance = useCallback(() => {
    // Always bump, even on the paths that bail out below, so the scheduling
    // effect re-runs and the next slide is timed.
    setSlideKey((key) => key + 1);

    const items = poolRef.current;
    if (items.length === 0) {
      setCurrent(null);
      setUpcomingIds([]);
      return;
    }

    const pick = (): string | undefined => {
      while (priorityRef.current.length > 0) {
        const id = priorityRef.current.shift();
        if (id && items.some((m) => m.id === id)) return id;
      }
      // Drop ids that were hidden or deleted since the queue was built.
      queueRef.current = queueRef.current.filter((id) => items.some((m) => m.id === id));
      if (queueRef.current.length === 0) refill(items.length > 1 ? current?.id : undefined);
      return queueRef.current.shift();
    };

    const nextId = pick();
    const next = items.find((m) => m.id === nextId) ?? items[0] ?? null;
    // Keep the object identity when it is the same photo, so the stage only
    // re-renders when the picture actually changes.
    setCurrent((prev) => (prev && next && prev.id === next.id ? prev : next));
    setUpcomingIds([...priorityRef.current, ...queueRef.current].slice(0, 2));
  }, [current?.id, refill]);

  // Track arrivals so brand-new uploads can jump the queue.
  useEffect(() => {
    if (knownIdsRef.current === null) {
      knownIdsRef.current = new Set(pool.map((m) => m.id));
      return;
    }
    const known = knownIdsRef.current;
    const arrivals = pool.filter((m) => !known.has(m.id));
    for (const item of pool) known.add(item.id);
    if (newMediaPriority && arrivals.length > 0) {
      priorityRef.current.push(...arrivals.map((m) => m.id));
    }
  }, [pool, newMediaPriority]);

  // Start, and recover if whatever was on screen disappeared.
  useEffect(() => {
    if (pool.length === 0) {
      setCurrent(null);
      return;
    }
    if (!current || !pool.some((m) => m.id === current.id)) advance();
  }, [pool, current, advance]);

  // Images advance on a timer; videos advance when they end, with this as a
  // safety net in case playback stalls on the TV's browser.
  // `slideKey` is in the deps on purpose: it changes on every advance, so the
  // next slide is always scheduled - including when the same item repeats.
  useEffect(() => {
    if (!current) return;
    const holdMs =
      current.kind === 'video'
        ? Math.max(current.durationMs ?? slideDurationMs, slideDurationMs) + 4000
        : slideDurationMs;
    const timer = setTimeout(advance, holdMs);
    return () => clearTimeout(timer);
  }, [current, slideKey, slideDurationMs, advance]);

  const upcoming = useMemo(
    () =>
      upcomingIds
        .map((id) => pool.find((m) => m.id === id))
        .filter((m): m is MediaItem => Boolean(m)),
    [upcomingIds, pool],
  );

  return { current, upcoming, slideKey, advance };
}
